<template>
    <div>
        <base-header class="pb-1">
            <div class="row align-items-center py-4">
                <div class="col-lg-6 col-7">
                    <h6 class="h2 text-white d-inline-block mb-0">Meu Perfil</h6>
                </div>
            </div>
        </base-header>

        <div class="container-fluid mt--6">
            <div v-if="user.phone && !phoneConfirmed && !requiresMfa" class="alert alert-warning">
                Seu celular ainda não está confirmado. Confirme para usar MFA no esqueci senha.
                <base-button type="warning" size="sm" class="ml-2" @click="initiatePhoneConfirmation"
                    :disabled="loading">
                    Confirmar celular
                </base-button>
            </div>

            <form class="needs-validation" @submit.prevent="updateProfile">
                <card>
                    <h3 slot="header" class="mb-0">Dados do Perfil</h3>
                    <div class="form-row">
                        <div class="col-md-6">
                            <base-input label="Nome" name="name" placeholder="Nome completo" v-model="user.name">
                            </base-input>
                        </div>
                        <div class="col-md-6">
                            <base-input label="Telefone" name="phone" placeholder="Telefone" v-model="user.phone"
                                v-mask="['(##) #####-####']" :disabled="mfaMode === 'confirm'">
                            </base-input>
                            <small class="form-text text-muted" v-if="phoneChanged && mfaMode !== 'update'">
                                O telefone foi alterado. Será necessário confirmar via MFA.
                            </small>
                            <small class="form-text text-success" v-if="phoneConfirmed && !phoneChanged">
                                Celular confirmado.
                            </small>
                        </div>
                    </div>
                    <div class="form-row">
                        <div class="col-md-6">
                            <base-input label="Nova Senha" name="newPassword"
                                placeholder="Nova senha (mínimo 6 caracteres)" v-model="user.newPassword"
                                type="password" minlength="6" :disabled="!!mfaMode">
                            </base-input>
                            <small class="form-text text-muted">
                                Deixe em branco se não desejar alterar a senha.
                            </small>
                        </div>
                    </div>

                    <div v-if="requiresMfa" class="form-row mt-3">
                        <div class="col-md-12">
                            <div class="alert alert-info">
                                <strong>Código MFA enviado!</strong>
                                <span v-if="mfaMode === 'confirm'">
                                    Informe o código recebido por SMS para confirmar o celular cadastrado.
                                </span>
                                <span v-else>
                                    Informe o código recebido por SMS para confirmar a alteração do telefone.
                                </span>
                            </div>
                        </div>
                        <div class="col-md-6">
                            <base-input label="Código MFA" name="mfaCode" placeholder="Código de 6 dígitos"
                                v-model="mfaCode" v-mask="'######'" maxlength="6">
                            </base-input>
                        </div>
                    </div>
                </card>

                <div class="col-md-12 text-center mb-3">
                    <base-button type="primary" native-type="submit"
                        :disabled="loading || (requiresMfa && (!mfaCode || mfaCode.length !== 6))">
                        {{ loading ? 'Processando...' : (mfaMode === 'confirm' ? 'Confirmar celular' : 'Atualizar Perfil') }}
                    </base-button>
                    <base-button type="secondary" native-type="button" @click="cancelMfa" class="ml-2"
                        v-if="requiresMfa">
                        Cancelar
                    </base-button>
                </div>
            </form>

            <card class="mt-4">
                <h3 slot="header" class="mb-0">Alterar Senha</h3>
                <div class="form-row">
                    <div class="col-md-4">
                        <base-input label="Nova Senha" name="quickPassword"
                            placeholder="Nova senha (mínimo 6 caracteres)" v-model="quickPassword" type="password"
                            minlength="6">
                        </base-input>
                    </div>
                </div>
                <div class="col-md-12 text-center mb-3">
                    <base-button type="primary" native-type="button" @click="changePasswdQuick"
                        :disabled="loading || !quickPassword || quickPassword.length < 6">
                        Modificar Senha
                    </base-button>
                </div>
            </card>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            user: {
                name: '',
                phone: '',
                newPassword: ''
            },
            originalPhone: '',
            phoneConfirmed: false,
            quickPassword: '',
            mfaTransactionId: null,
            mfaCode: '',
            mfaMode: null,
            loading: false
        };
    },
    computed: {
        phoneChanged() {
            const current = (this.user.phone || '').replace(/\D/g, '');
            const original = (this.originalPhone || '').replace(/\D/g, '');
            return current && current !== original;
        },
        requiresMfa() {
            return !!this.mfaMode;
        }
    },
    mounted() {
        this.loadUserData();
    },
    methods: {
        loadUserData() {
            this.$clubApi.get('/user/data')
                .then((response) => {
                    const userData = response.data.object;
                    this.user.name = userData.name || '';
                    this.user.phone = userData.phone || '';
                    this.originalPhone = userData.phone || '';
                    this.phoneConfirmed = !!userData.phoneConfirmed;
                    if (userData.name) {
                        localStorage.setItem('name', userData.name);
                    }
                    if (userData.profile) {
                        localStorage.setItem('profile', userData.profile);
                    }
                })
                .catch((error) => {
                    this.$notify({
                        type: 'warning',
                        message: (error.response && error.response.data && error.response.data.msg)
                            || 'Erro ao carregar dados do usuário'
                    });
                })
                .finally(() => {
                    NProgress.done();
                });
        },
        updateProfile() {
            if (this.mfaMode === 'confirm') {
                this.confirmPhoneConfirmation();
                return;
            }

            if (this.mfaMode === 'update') {
                this.confirmPhoneUpdate();
                return;
            }

            if (this.phoneChanged) {
                this.initiatePhoneUpdate();
                return;
            }

            if (!this.user.name && !this.user.newPassword) {
                this.$notify({ type: 'warning', message: 'Informe pelo menos um campo para atualizar.' });
                return;
            }

            if (this.user.newPassword && this.user.newPassword.length < 6) {
                this.$notify({ type: 'warning', message: 'A senha deve ter no mínimo 6 caracteres.' });
                return;
            }

            this.saveProfileFields();
        },
        saveProfileFields() {
            this.loading = true;
            const request = {};
            if (this.user.name) request.name = this.user.name;
            if (this.user.newPassword) request.newPassword = this.user.newPassword;

            this.$clubApi.post('/user/backoffice/profile', request)
                .then(() => {
                    this.$notify({ type: 'success', message: 'Perfil atualizado com sucesso!' });
                    this.user.newPassword = '';
                    this.loadUserData();
                })
                .catch((error) => {
                    this.$notify({
                        type: 'warning',
                        message: (error.response && error.response.data && error.response.data.msg)
                            || 'Erro ao atualizar perfil'
                    });
                })
                .finally(() => {
                    this.loading = false;
                    NProgress.done();
                });
        },
        initiatePhoneUpdate() {
            if (!this.user.phone) {
                this.$notify({ type: 'warning', message: 'Informe o novo telefone.' });
                return;
            }

            this.loading = true;
            this.$clubApi.post('/user/backoffice/phone/update/initiate', {
                phone: this.user.phone.replace(/\D/g, '')
            })
                .then((response) => {
                    const transactionId = response.data.object;
                    if (transactionId) {
                        this.mfaTransactionId = transactionId;
                        this.mfaMode = 'update';
                        this.$notify({
                            type: 'success',
                            message: 'Código MFA enviado por SMS para o novo telefone!'
                        });
                    }
                })
                .catch((error) => {
                    this.$notify({
                        type: 'warning',
                        message: (error.response && error.response.data && error.response.data.msg)
                            || 'Erro ao iniciar atualização de telefone'
                    });
                })
                .finally(() => {
                    this.loading = false;
                    NProgress.done();
                });
        },
        confirmPhoneUpdate() {
            if (!this.mfaCode || this.mfaCode.length !== 6) {
                this.$notify({ type: 'warning', message: 'Informe o código MFA para confirmar a alteração do telefone.' });
                return;
            }

            this.loading = true;
            this.$clubApi.post('/user/backoffice/phone/update/confirm', {
                phone: this.user.phone.replace(/\D/g, ''),
                mfaTransactionId: this.mfaTransactionId,
                mfaCode: this.mfaCode
            })
                .then(() => {
                    this.$notify({ type: 'success', message: 'Celular atualizado e confirmado com sucesso!' });
                    const shouldUpdateProfile = !!(this.user.name || this.user.newPassword);
                    this.clearMfaState();
                    this.originalPhone = (this.user.phone || '').replace(/\D/g, '')
                        ? this.user.phone
                        : this.originalPhone;
                    this.phoneConfirmed = true;

                    if (shouldUpdateProfile) {
                        this.saveProfileFields();
                    } else {
                        this.loadUserData();
                    }
                })
                .catch((error) => {
                    this.$notify({
                        type: 'warning',
                        message: (error.response && error.response.data && error.response.data.msg)
                            || 'Erro ao confirmar atualização de telefone'
                    });
                })
                .finally(() => {
                    this.loading = false;
                    NProgress.done();
                });
        },
        initiatePhoneConfirmation() {
            this.loading = true;
            this.$clubApi.post('/user/backoffice/phone/confirm/initiate')
                .then((response) => {
                    const transactionId = response.data.object;
                    if (transactionId) {
                        this.mfaTransactionId = transactionId;
                        this.mfaMode = 'confirm';
                        this.$notify({
                            type: 'success',
                            message: 'Código MFA enviado por SMS para o celular cadastrado!'
                        });
                    }
                })
                .catch((error) => {
                    this.$notify({
                        type: 'warning',
                        message: (error.response && error.response.data && error.response.data.msg)
                            || 'Erro ao iniciar confirmação de celular'
                    });
                })
                .finally(() => {
                    this.loading = false;
                    NProgress.done();
                });
        },
        confirmPhoneConfirmation() {
            if (!this.mfaCode || this.mfaCode.length !== 6) {
                this.$notify({ type: 'warning', message: 'Informe o código MFA para confirmar o celular.' });
                return;
            }

            this.loading = true;
            this.$clubApi.post('/user/backoffice/phone/confirm', {
                mfaTransactionId: this.mfaTransactionId,
                mfaCode: this.mfaCode
            })
                .then(() => {
                    this.$notify({ type: 'success', message: 'Celular confirmado com sucesso!' });
                    this.clearMfaState();
                    this.phoneConfirmed = true;
                    this.loadUserData();
                })
                .catch((error) => {
                    this.$notify({
                        type: 'warning',
                        message: (error.response && error.response.data && error.response.data.msg)
                            || 'Erro ao confirmar celular'
                    });
                })
                .finally(() => {
                    this.loading = false;
                    NProgress.done();
                });
        },
        changePasswdQuick() {
            if (!this.quickPassword || this.quickPassword.length < 6) {
                this.$notify({ type: 'warning', message: 'A senha deve ter no mínimo 6 caracteres.' });
                return;
            }

            this.loading = true;
            this.$clubApi.post('/user/update/passwd', {
                passwd: this.quickPassword
            })
                .then(() => {
                    this.$notify({ type: 'success', message: 'Senha atualizada com sucesso!' });
                    this.quickPassword = '';
                })
                .catch((error) => {
                    this.$notify({
                        type: 'warning',
                        message: (error.response && error.response.data && error.response.data.msg)
                            || 'Erro ao atualizar senha'
                    });
                })
                .finally(() => {
                    this.loading = false;
                    NProgress.done();
                });
        },
        clearMfaState() {
            this.mfaMode = null;
            this.mfaTransactionId = null;
            this.mfaCode = '';
        },
        cancelMfa() {
            this.clearMfaState();
            if (this.phoneChanged) {
                this.user.phone = this.originalPhone;
            }
        }
    }
}
</script>

<style></style>
